import OpenAI from 'openai';
import type { Model, ModelMetadata, ModelProvider } from '../../model';
import { OpenAIChatCompletionsModel } from '../openai/chat-completions-model';
import {
  getDefaultDashScopeBaseURL,
  getDefaultDashScopeKey,
  getDefaultDashScopeModel,
} from './defaults';

export type DashScopeModelMetadataResolver =
  | Record<string, ModelMetadata>
  | ((modelName: string) => ModelMetadata | undefined);

export type DashScopeProviderOptions = {
  apiKey?: string;
  baseURL?: string;
  defaultModel?: string;
  openAIClient?: OpenAI;
  modelMetadata?: DashScopeModelMetadataResolver;
};

export class DashScopeProvider implements ModelProvider {
  #client?: OpenAI;
  #modelCache = new Map<string, Model>();
  #options: DashScopeProviderOptions;

  constructor(options: DashScopeProviderOptions = {}) {
    this.#options = options;
    if (this.#options.openAIClient) {
      if (this.#options.apiKey) {
        throw new Error('Cannot provide both apiKey and openAIClient');
      }
      if (this.#options.baseURL) {
        throw new Error('Cannot provide both baseURL and openAIClient');
      }
      this.#client = this.#options.openAIClient;
    }
  }

  getDefaultModelName(): string {
    return this.#options.defaultModel || getDefaultDashScopeModel();
  }

  getModelMetadata(modelName?: string): ModelMetadata | undefined {
    const resolvedModelName = modelName || this.getDefaultModelName();
    return resolveConfiguredModelMetadata(
      this.#options.modelMetadata,
      resolvedModelName,
    );
  }

  #getClient(): OpenAI {
    if (!this.#client) {
      this.#client = new OpenAI({
        apiKey: this.#options.apiKey ?? getDefaultDashScopeKey(),
        baseURL: this.#options.baseURL ?? getDefaultDashScopeBaseURL(),
      });
    }
    return this.#client;
  }

  async getModel(modelName?: string | undefined): Promise<Model> {
    const model = modelName || this.getDefaultModelName();
    const cacheKey = JSON.stringify([model, 'dashscope_chat_completions']);

    const cachedModel = this.#modelCache.get(cacheKey);
    if (cachedModel) {
      return cachedModel;
    }

    const resolvedModel = new OpenAIChatCompletionsModel(
      this.#getClient(),
      model,
      this.getModelMetadata(model),
    );
    this.#modelCache.set(cacheKey, resolvedModel);
    return resolvedModel;
  }

  /**
   * Closes cached model wrappers and clears cache.
   */
  async close(): Promise<void> {
    const cachedModels = Array.from(new Set(this.#modelCache.values()));
    this.#modelCache.clear();

    const closeErrors: unknown[] = [];
    await Promise.all(
      cachedModels.map(async (model) => {
        const maybeClose = (model as { close?: () => Promise<void> | void })
          .close;
        if (typeof maybeClose !== 'function') {
          return;
        }
        try {
          await maybeClose.call(model);
        } catch (error) {
          closeErrors.push(error);
        }
      }),
    );

    if (closeErrors.length === 1) {
      throw closeErrors[0];
    }
    if (closeErrors.length > 1) {
      const error = new Error('Failed to close DashScopeProvider.');
      (error as Error & { causes?: unknown[] }).causes = closeErrors;
      throw error;
    }
  }
}

function resolveConfiguredModelMetadata(
  resolver: DashScopeModelMetadataResolver | undefined,
  modelName: string,
): ModelMetadata | undefined {
  if (!resolver) {
    return undefined;
  }
  if (typeof resolver === 'function') {
    return resolver(modelName);
  }
  return resolver[modelName] ?? resolver[modelName.toLowerCase()];
}
